<template>
  <chart-container :titleChart="titleChart" @zoomEnabled="zoomEnabled" @zoomDisabled="zoomDisabled" :zoomGraphValue="zoomGraphValue" :downloadId="downloadId" :downloadShow="false" :isZoomEnabled="items && items.length > 0">
    <div slot="body">
      <div v-if="items && items.length > 0" :class="downloadId == 'expected-data' && zoomTrigger ? 'd-flex' : ''">
        <div :style="`max-height: ${height}; width: 100%`" v-on:scroll="loadMore" class="edro-dashboard-table mt-10" ref="infiniteScrollComponent">
          <b-table :fixed="!zoomTrigger" :items="items" :fields="fields" :sort-compare="mySortCompare" :sort-desc.sync="sortDesc" :sort-by.sync="sortBy">
            <template v-for="(field, i) in fields" :slot="`HEAD_${field.key}`" slot-scope="data">
              <div :key="i">
                {{ data.label }}
                <sort-icon :data="data" :sortBy="sortBy" :sortDesc="sortDesc"></sort-icon>
              </div>
            </template>

            <template slot="is_participant_active" slot-scope="data">
              <i class="fa fa-asterisk" style="color: #9b9b9b" v-if="data.value == 'false'"></i>
            </template>

            <template slot="participantId" slot-scope="data">
              <div v-if="data.value.length > stringTruncateAt" v-b-tooltip.hover="{ placement: 'right', title: data.value }">{{ data.value.slice(0, stringTruncateAt) }}<span class="font-weight-bold">...</span></div>
              <div v-else>
                {{ data.value }}
              </div>
            </template>

            <template slot="HEAD_fvc" slot-scope="data">
              <span>
                {{ data.label }}
                <div class="table-custom-tooltip" @mouseover="scrollToTooltipElement('fvc-tooltip')">
                  <i class="fa fa-info-circle" />
                  <span class="table-custom-tooltiptext" id="fvc-tooltip"> <strong>FVC -</strong> Actual <strong>Forced Vital Capacity</strong> representing the full amount of air that can be exhaled with effort in a complete breath.</span>
                </div>
                <sort-icon :data="data" :sortBy="sortBy" :sortDesc="sortDesc"></sort-icon>
              </span>
            </template>

            <template slot="HEAD_fev1" slot-scope="data">
              <span>
                {{ data.label }}
                <div class="table-custom-tooltip" @mouseover="scrollToTooltipElement('fev1-tooltip')">
                  <i class="fa fa-info-circle" />
                  <span class="table-custom-tooltiptext" id="fev1-tooltip"> <strong>FEV1 -</strong> Actual <strong>Forced Expiratory Volume</strong> representing the amount of exhaled air during the first second of the spirometry test.</span>
                </div>
                <sort-icon :data="data" :sortBy="sortBy" :sortDesc="sortDesc"></sort-icon>
              </span>
            </template>

            <template slot="HEAD_fev1/fvc" slot-scope="data">
              <span>
                {{ data.label }}
                <div class="table-custom-tooltip" @mouseover="scrollToTooltipElement('fev1-fvc-tooltip')">
                  <i class="fa fa-info-circle" />
                  <span class="table-custom-tooltiptext" id="fev1-fvc-tooltip"> <strong>FEV1 FVC -</strong> Actual <strong>Forced Expiratory Volume</strong> over <strong>Forced Vital Capacity</strong> representing the ratio for the amount of air you can forcefully exhale.</span>
                </div>
                <sort-icon :data="data" :sortBy="sortBy" :sortDesc="sortDesc"></sort-icon>
              </span>
            </template>

            <template slot="HEAD_fev6" slot-scope="data">
              <span>
                {{ data.label }}
                <div class="table-custom-tooltip" @mouseover="scrollToTooltipElement('fev6-tooltip')">
                  <i class="fa fa-info-circle" />
                  <span class="table-custom-tooltiptext" id="fev6-tooltip"> <strong>FEV6 -</strong> Actual <strong>Forced Expiratory Volume</strong> representing the amount of air exhaled during the six-second spirometry test.</span>
                </div>
                <sort-icon :data="data" :sortBy="sortBy" :sortDesc="sortDesc"></sort-icon>
              </span>
            </template>

            <template slot="HEAD_fef2575" slot-scope="data">
              <span>
                {{ data.label }}
                <div class="table-custom-tooltip" @mouseover="scrollToTooltipElement('fef2575-tooltip')">
                  <i class="fa fa-info-circle" />
                  <span class="table-custom-tooltiptext" id="fef2575-tooltip">
                    <strong>FEF2575 - </strong>
                    <strong>Forced Expiratory Flow</strong> measured at 25% and 75% of the pulmonary volume representing the mean forced expiratory flow rate.</span
                  >
                </div>
                <sort-icon :data="data" :sortBy="sortBy" :sortDesc="sortDesc"></sort-icon>
              </span>
            </template>

            <template slot="HEAD_pef" slot-scope="data">
              <span>
                {{ data.label }}
                <div class="table-custom-tooltip" @mouseover="scrollToTooltipElement('pef-tooltip')">
                  <i class="fa fa-info-circle" />
                  <span class="table-custom-tooltiptext" id="pef-tooltip"> <strong>PEF -</strong> Actual <strong>Peak Expiratory Flow</strong> representing the maximum speed of expiration.</span>
                </div>
                <sort-icon :data="data" :sortBy="sortBy" :sortDesc="sortDesc"></sort-icon>
              </span>
            </template>
          </b-table>
        </div>
        <div v-if="downloadId == 'expected-data'" class="min-height-0 pl-0 pb-0 d-flex" :class="zoomTrigger ? 'my-auto ml-50 mr-50' : 'remove-elem-download text-center'">
          <div>
            <i class="fa fa-asterisk mr-5" style="color: #9b9b9b"></i>
          </div>
          <div>Participant is Not Active</div>
        </div>
      </div>
      <div v-else class="align-center p-60">
        <div class="card-text-title no-data" v-if="dataLoaded">No Data In</div>
      </div>
    </div>
  </chart-container>
</template>

<script>
import ChartContainer from "./chart-container.vue";
import moment from "moment";
import SortIcon from "@/components/common/sort-icon.vue";

export default {
  components: {
    ChartContainer,
    SortIcon
  },

  props: {
    studyID: { type: String, default: "" },
    titleChart: [String],
    zoomGraphValue: { type: String, default: "" },
    downloadId: { type: String, default: "" },
    fields: { type: Array, default: () => [] },
    items: { type: Array, default: () => [] },
    dataLoaded: { type: Boolean, default: false },
    infiniteScroll: { type: Boolean, default: false }
  },

  watch: {
    zoomTrigger: function() {
      this.height = this.zoomTrigger ? "550px" : "240px";
      if (this.zoomTrigger) {
        this.stringTruncateAt = this.downloadId == "expected-data" ? 40 : 50;
      } else {
        this.stringTruncateAt = this.downloadId == "expected-data" ? 8 : 16;
      }
    },
    zoomGraphValue: function(newVal) {
      this.zoomGraphValueChild = newVal;
      if (newVal == "") {
        this.zoomDisabled();
      }
    }
  },

  data() {
    return {
      height: "240px",
      zoomTrigger: false,
      stringTruncateAt: this.downloadId == "expected-data" ? 8 : 16,
      previousScrollPosition: 0,
      sortBy: null,
      sortDesc: false
    };
  },

  methods: {
    zoomEnabled() {
      this.$emit("zoomEnabled");
      this.zoomTrigger = true;
    },
    zoomDisabled() {
      this.$emit("zoomDisabled");
      this.zoomTrigger = false;
    },
    mySortCompare(a, b, key) {
      if (!["expected_missing_date", "most_recent_data", "startDate"].includes(key)) {
        return null;
      }
      let aDate = moment(a[key], "DD MMM YYYY");
      let bDate = moment(b[key], "DD MMM YYYY");

      if (key == "startDate") {
        aDate = moment(a[key], "DD MMM YYYY T HH:mm:ss");
        bDate = moment(b[key], "DD MMM YYYY T HH:mm:ss");
      }
      if (aDate.isValid && bDate.isValid) {
        if (aDate < bDate) {
          return -1;
        } else if (aDate > bDate) {
          return 1;
        } else {
          return 0;
        }
      }
      return null;
    },
    loadMore() {
      if (this.infiniteScroll) {
        const scrollPosition = this.$refs.infiniteScrollComponent.clientHeight + this.$refs.infiniteScrollComponent.scrollTop;

        if (this.$refs.infiniteScrollComponent.scrollTop !== 0 && scrollPosition > this.$refs.infiniteScrollComponent.scrollHeight - 1 && scrollPosition > this.previousScrollPosition) {
          this.previousScrollPosition = scrollPosition;
          this.$emit("onScrollRecentDataTable");
          this.$refs.infiniteScrollComponent.scrollTop = this.$refs.infiniteScrollComponent.scrollHeight - this.$refs.infiniteScrollComponent.clientHeight;
        }
      }
    },
    scrollToTooltipElement(elementId) {
      document.querySelector("#" + elementId).scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }
};
</script>

<style lang="scss" scoped>
.table-custom-tooltip {
  position: relative;
  display: inline-block;

  .table-custom-tooltiptext {
    font-size: 12px;
    font-family: "RobotoRegular";
    visibility: hidden;
    width: 241px;
    white-space: break-spaces;
    background-color: #eff3f5;
    color: #666669;
    text-align: left;
    border-radius: 10px;
    padding: 10px 12px 11px 12px;
    font-weight: 500;
    position: absolute;
    z-index: 1;
    top: -6px;
    margin-left: 8px;
    left: 105%;
    box-shadow: -1px 4px 12px -2px rgba(0, 0, 0, 0.44);

    &::after {
      content: " ";
      position: absolute;
      top: 8px;
      right: 100%;
      height: 10px;
      margin-top: -5px;
      border-width: 10px;
      border-style: solid;
      border-color: transparent #eff3f5 transparent transparent;
    }
  }

  &:hover .table-custom-tooltiptext {
    visibility: visible;
  }
}

i.fa.fa-info-circle {
  margin: 0 2.5px !important;
}
</style>
